import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import type { CarePlan, Task } from "fhir/r4";

interface CreateRecommendationsParams {
  recommendationDescriptions: string[];
  addAfterTaskId?: string;
}

interface CreateRecommendationsResponse {
  carePlan: CarePlan;
  recommendations: Task[];
}

export const useCreateRecommendations = ({
  encounterId,
  carePlanId,
}: {
  encounterId: string;
  carePlanId: string;
}) => {
  const queryClient = useQueryClient();
  const { patientId } = useParams({ from: "/patient/$patientId" });
  return useApiMutation<
    CreateRecommendationsResponse,
    CreateRecommendationsParams
  >({
    mutationOptions: {
      mutationKey:
        QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.Recommendation.CREATE_MULTIPLE(
          {
            patientId,
            encounterId,
            carePlanId,
          },
        ),
      mutationFn: (params) => {
        return axiosInstance.post(
          `/charting/careplan/${carePlanId}/recommendations/`,
          {
            recommendations: params.recommendationDescriptions.map(
              (description) => ({
                recommendationDescription: description,
              }),
            ),
            addAfterTaskId: params.addAfterTaskId,
          },
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.Recommendation.DEFAULT(
              {
                patientId,
                encounterId,
                carePlanId,
              },
            ),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.SUMMARY({
            patientId,
            encounterId,
          }),
        });
      },
    },
  });
};
